import { useState, useEffect, useRef, useCallback } from "react";
import {
  getTurnstileToken,
  prewarmTurnstileToken,
  takeReadyTurnstileToken,
  TURNSTILE_HEADER,
} from "@/lib/turnstile";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export interface AddressResult {
  displayName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  /**
   * Task #7002 — explicit country-level flag stamped by the api-server
   * (/api/address/search). When true this result represents an entire
   * country. Prefer this over inferring "country" from empty city/state,
   * which silently breaks if the geocoder ever returns a region name in
   * `state` for a country result.
   */
  isCountry?: boolean;
}

export function useAddressAutocomplete() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressResult[]>([]);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const queryRef = useRef("");
  const lookupRef = useRef<{ id: number; promise: Promise<AddressResult[]> } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const invalidate = useCallback(() => {
    requestIdRef.current++;
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    lookupRef.current = null;
  }, []);

  useEffect(() => {
    // Task #3502 — warm a bot-challenge token in the background so the
    // first keystroke search can attach one without blocking on the widget.
    prewarmTurnstileToken();
    return () => {
      invalidate();
    };
  }, [invalidate]);

  const lookup = useCallback((q: string, currentId: number) => {
    if (lookupRef.current?.id === currentId) return lookupRef.current.promise;
    const controller = new AbortController();
    abortRef.current = controller;
    let timeout: ReturnType<typeof setTimeout>;
    const promise = Promise.race([
      (async () => {
      try {
        // Task #7240 — search context: disable country bias so international
        // venue/city queries work correctly regardless of the visitor's country.
        const url = `${API_BASE}/address/search?q=${encodeURIComponent(q)}&context=search`;
        const doFetch = (tsToken: string | null) =>
          fetch(url, { signal: controller.signal, headers: tsToken ? { [TURNSTILE_HEADER]: tsToken } : {} });
        // Task #3228 / #3502 — attach a pre-warmed Turnstile token if one is
        // ready, WITHOUT blocking each keystroke on the widget. Only when
        // the server actively demands a challenge (403 `turnstile_required`)
        // do we fetch one and retry once.
        let res = await doFetch(takeReadyTurnstileToken());
        if (res.status === 403) {
          const errBody = await res.clone().json().catch(() => ({}) as { code?: string });
          if (errBody?.code === "turnstile_required") {
            const retryToken = await getTurnstileToken();
            if (currentId !== requestIdRef.current || controller.signal.aborted) return [];
            if (retryToken) res = await doFetch(retryToken);
          }
        }
        // If a newer request was scheduled after this one fired, the
        // newer request owns the final state transition — just drop
        // this response. (setSearching stays true; the newer request
        // will clear it.)
        if (currentId !== requestIdRef.current) return [];
        if (!res.ok) throw new Error("Place lookup failed");
        const data = await res.json().catch(() => null);
        if (currentId !== requestIdRef.current) return [];
        if (!Array.isArray(data?.results)) throw new Error("Place lookup failed");
        const results = Array.isArray(data?.results) ? data.results : [];
        return results as AddressResult[];
      } catch (error) {
        throw error;
      }
      })(),
      new Promise<AddressResult[]>((_, reject) => {
        timeout = setTimeout(() => {
          controller.abort();
          reject(new Error("Place lookup timed out"));
        }, 8_000);
      }),
    ]).then(results => {
      if (currentId === requestIdRef.current) setSuggestions(results);
      return results;
    }).catch(error => {
      if (currentId === requestIdRef.current) lookupRef.current = null;
      throw error;
    }).finally(() => {
      clearTimeout(timeout);
      if (currentId === requestIdRef.current) setSearching(false);
    });
    lookupRef.current = { id: currentId, promise };
    return promise;
  }, []);

  const search = useCallback((q: string) => {
    invalidate();
    queryRef.current = q;
    setQuery(q);
    setSuggestions([]);
    setSearching(q.trim().length >= 3);
    if (q.trim().length < 3) return;
    const id = requestIdRef.current;
    timerRef.current = setTimeout(() => {
      void lookup(q.trim(), id).catch(() => {});
    }, 350);
  }, [invalidate, lookup]);

  /** Deliberate submission bypasses debounce and shares a current-query lookup. */
  const resolve = useCallback((q: string) => {
    if (queryRef.current !== q) search(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 3) return Promise.resolve([]);
    if (!lookupRef.current) setSearching(true);
    return lookup(q.trim(), requestIdRef.current);
  }, [search, lookup]);

  const clear = useCallback(() => {
    invalidate();
    queryRef.current = "";
    setSuggestions([]);
    setQuery("");
    setSearching(false);
  }, [invalidate]);

  const setQueryDirect = useCallback((q: string) => {
    invalidate();
    queryRef.current = q;
    setSuggestions([]);
    setSearching(false);
    setQuery(q);
  }, [invalidate]);

  return { query, search, suggestions, searching, clear, setQueryDirect, resolve };
}
